/**
 * Environment Variable Validation
 * Validates required environment variables and provides helpful error messages
 */

interface EnvCheckResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export function checkEnvironmentVariables(): EnvCheckResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check VITE_API_URL
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    warnings.push(
      'VITE_API_URL is not set. Using default: http://localhost:3001. ' +
      'Set this in Vercel environment variables for production.'
    );
  } else if (apiUrl === 'http://localhost:3001' && import.meta.env.PROD) {
    errors.push(
      'VITE_API_URL is set to localhost in production! ' +
      'Update the environment variable in Vercel to point to your Railway backend.'
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

export function logEnvironmentCheck(): void {
  const result = checkEnvironmentVariables();

  if (!import.meta.env.PROD) {
    // Development mode - just show warnings
    if (result.warnings.length > 0) {
      console.groupCollapsed('%c⚠️ Environment Warnings', 'color: orange; font-weight: bold');
      result.warnings.forEach(warning => console.warn(warning));
      console.groupEnd();
    }
    return;
  }

  // Production mode - show errors prominently
  if (result.errors.length > 0) {
    console.group('%c❌ Environment Configuration Errors', 'color: red; font-weight: bold; font-size: 16px');
    result.errors.forEach(error => console.error(error));
    console.log('%cTo fix:', 'font-weight: bold');
    console.log('1. Go to your Vercel project settings');
    console.log('2. Navigate to Environment Variables');
    console.log('3. Set VITE_API_URL to your Railway backend URL');
    console.log('4. Redeploy your application');
    console.groupEnd();
  }

  if (result.warnings.length > 0) {
    console.groupCollapsed('%c⚠️ Environment Warnings', 'color: orange; font-weight: bold');
    result.warnings.forEach(warning => console.warn(warning));
    console.groupEnd();
  }

  if (result.isValid && result.warnings.length === 0) {
    console.log('%c✅ Environment configuration looks good', 'color: green; font-weight: bold');
  }
}
